import { create } from 'zustand';
import type { AutomationTarget, Clip, Project, SnapValue, Track, TrackKind } from '@/domain/types';
import { createEmptyProject } from '@/domain/project';
import { cloneProject } from '@/domain/format/serialize';
import { clipEndTicks, projectEndTicks, tempoMapOf } from '@/domain/selectors';
import { archiveAsTake } from '@/commands/takeCommands';
import { secondsSpanToTicks, secondsToTicks, ticksToSeconds } from '@/timing/tempoMap';
import { command, runCommand } from '@/commands/registry';
import type { CommandRecord } from '@/commands/types';
import * as ClipCommands from '@/commands/clipCommands';
import * as TrackCommands from '@/commands/trackCommands';
import * as TransportCommands from '@/commands/transportCommands';
import * as EffectCommands from '@/commands/effectCommands';
import * as MidiCommands from '@/commands/midiCommands';
import { AudioImportError } from '@/assets/AssetManager';
import * as SamplerCommands from '@/commands/samplerCommands';
import * as CrossfadeCommands from '@/commands/crossfadeCommands';
import { bareOverlaps } from '@/domain/overlap';
import * as ClipGainCommands from '@/commands/clipGainCommands';
import { type ClipGainClipboard, copyClipGain } from '@/domain/clipGain';
import * as FreezeCommands from '@/commands/freezeCommands';
import * as RangeCommands from '@/commands/rangeCommands';
import { freezeRange, freezeSignature, trackChannelCount } from '@/domain/freeze';
import { bufferToChannels, renderProject } from '@/engine/render';
import { encodeWav } from '@/util/wav';
import { instrumentParamDescriptor } from '@/engine/instruments/descriptors';
import { isWamUrl } from '@/engine/effects/wamHost';
import { newEffectId } from '@/util/id';
import { normaliseParam } from '@/engine/effects/descriptors';
import { assignRootPitches } from '@/engine/instruments/samplerState';
import { RecordingError, Recorder } from '@/engine/Recorder';
import { MidiInput, type MidiAvailability, type MidiDevice } from '@/engine/MidiInput';
import { SustainPedal } from '@/engine/sustainPedal';
import type { TransportState } from '@/engine/AudioEngine';
import { DEFAULT_BEND_RANGE_SEMITONES } from '@/storage/midiPerformance';
import { findRecoveryOffer, type RecoveryOffer } from '@/storage/autosave';
import type { ProjectSummary } from '@/storage/StorageProvider';
import { StorageError } from '@/storage/StorageProvider';
import { createId } from '@/util/id';
import { MidiFileError, decodeMidiFile } from '@/util/midiFile';
import { encodeProjectAsMidi, planMidiImport } from '@/storage/midiFileIO';
import { FileCancelled, downloadBlob, pickSaveTarget, writeFile } from '@/storage/fileIO';
import type { CopiedAutomation } from '@/commands/rangeCommands';
import { detectTempo } from '@/engine/tempoDetect';
import { detectBeats, numberBeats, warpMarkersForBeats } from '@/engine/beatTrack';
import { tempoMapFromBeats } from '@/domain/tempoFromBeats';
import { newWarpMarkerId } from '@/util/id';
import { measureLoudness } from '@/engine/loudness';
import { ensureAudio, getServices } from './services';
import { consolidatableTracks, consolidateRange } from '@/engine/consolidate';

/**
 * The application store.
 *
 * This is the only place where the domain, the audio engine and storage meet.
 * The rules it enforces:
 *
 *  - React never mutates a project. It dispatches a command.
 *  - Every project change flows to the engine and to autosave through one path,
 *    `commit`, so no code path can change state and forget to tell the audio.
 *  - The playhead is not in here. It moves 60 times a second and lives in
 *    `PlayheadClock` instead.
 */

export interface Toast {
  id: number;
  kind: 'info' | 'success' | 'warning' | 'error';
  title: string;
  detail?: string;
  /** Errors stay until dismissed; everything else fades. */
  sticky: boolean;
}

export interface Selection {
  clipIds: string[];
  trackId: string | null;
  /** The automation lane being edited, and the points selected within it. */
  laneId: string | null;
  pointIds: string[];
  /** Notes selected in the piano roll, within the clip being edited. */
  noteIds: string[];
  /**
   * A stretch of time across chosen tracks.
   *
   * Clip selection answers "which objects"; a range answers "which part of the
   * piece", which is what splicing, inserting and cropping operate on.
   */
  range: TimeRange | null;
}

export interface TimeRange {
  startTicks: number;
  endTicks: number;
  trackIds: string[];
}

export type ModalKind = 'none' | 'projects' | 'export' | 'shortcuts' | 'about' | 'storage';

export type EditTool = 'select' | 'split' | 'range';

export type RangeEdit =
  | 'split'
  | 'delete'
  | 'spliceOut'
  | 'insert'
  | 'crop'
  | 'duplicate'
  | 'copy'
  | 'pasteReplace'
  | 'pasteInsert';

/**
 * A stretch of time on the clipboard.
 *
 * Separate from the clip clipboard because the two answer different questions.
 * Copying clips takes the objects you chose; copying a range takes *everything
 * sounding between two points*, including the halves of clips that straddle the
 * edges, and remembers how long it was and how many tracks wide.
 */
export interface RangeClipboard {
  lengthTicks: number;
  trackCount: number;
  clips: Clip[];
  automation: CopiedAutomation[];
}

export interface DawState {
  project: Project;
  ready: boolean;
  audioReady: boolean;
  dirty: boolean;
  savedProjectId: string | null;

  transportState: TransportState;
  countingIn: boolean;
  recordingTrackId: string | null;

  selection: Selection;
  editTool: EditTool;
  modal: ModalKind;
  projectList: ProjectSummary[];
  recoveryOffer: RecoveryOffer | null;
  toasts: Toast[];
  clipboard: Clip[];
  undoLabel: string | null;
  redoLabel: string | null;
  lastAutosaveAt: number | null;
  inputDevices: { deviceId: string; label: string }[];

  midiStatus: MidiAvailability | 'open';
  /** How late everything arrives because of a plugin, and how far each track is held back to line up. */
  pluginLatencySec: number;
  heldBackSec: Record<string, number>;
  midiDevices: MidiDevice[];
  midiDeviceId: string | null;
  /** Octave the computer keyboard plays in when no controller is connected. */
  keyboardOctave: number;

  // -- lifecycle
  boot: () => Promise<void>;
  activateAudio: () => Promise<void>;

  // -- editing
  dispatch: (record: CommandRecord) => void;
  undo: () => void;
  redo: () => void;

  // -- view (not undoable: view state is not work)
  setView: (patch: Partial<Project['view']>) => void;
  setSnap: (snap: SnapValue) => void;
  setSelection: (selection: Partial<Selection>) => void;
  selectClips: (clipIds: string[], additive: boolean) => void;
  setEditTool: (tool: EditTool) => void;
  setRange: (range: TimeRange | null) => void;
  /** The range operations, all acting on the current selection. */
  applyRangeEdit: (kind: RangeEdit, allTracks?: boolean) => void;
  /** The stretch of time last copied, if any. */
  rangeClipboard: RangeClipboard | null;
  /**
   * When on, deleting a clip closes the gap it leaves on its own track, and
   * trimming its end moves what follows.
   */
  rippleEditing: boolean;
  /**
   * Crossfade an overlap the moment an edit creates one.
   *
   * Off by default, like ripple, and for the same reason: it changes what an
   * ordinary drag means, and a mode that changes that silently is a mode people
   * stop trusting. Overlaps are always *drawn* whether this is on or not —
   * seeing what happened is information, and fixing it is a decision.
   */
  autoFadeOverlaps: boolean;
  setRippleEditing: (on: boolean) => void;
  setAutoFadeOverlaps: (on: boolean) => void;
  /** Crossfades every uncovered overlap, and says how many there were. */
  fadeOverlaps: () => void;
  /**
   * When on, a click inside an audio clip draws its gain envelope instead of
   * selecting and dragging the clip. A mode rather than a modifier, because
   * riding a level is a stretch of work, not one click.
   */
  clipGainEditing: boolean;
  setClipGainEditing: (on: boolean) => void;
  setModal: (modal: ModalKind) => void;

  // -- transport
  play: () => void;
  pause: () => void;
  stop: () => void;
  togglePlay: () => void;
  seekTicks: (ticks: number) => void;
  toggleLoop: () => void;
  /**
   * The loop region and the range are two statements about a stretch of time:
   * "play this again" and "edit this". Each can be made from the other in one
   * step, because "loop what I have marked" and "mark what is looping" are the
   * two most common things to want next.
   */
  loopFromRange: () => void;
  rangeFromLoop: () => void;
  toggleMetronome: () => void;
  /** Sets the tempo of the event governing `tick` — the project tempo by default. */
  setTempo: (bpm: number, tick?: number) => void;

  // -- clipboard
  /**
   * A clip gain ride waiting to be put on another clip. Separate from the clip
   * clipboard because copying a clip and copying the shape drawn on it are
   * different intentions, and one should not quietly clear the other.
   */
  clipGainClipboard: ClipGainClipboard | null;
  copyClipGainRide: () => void;
  /** Puts the copied ride on the selected clips — where it was, or with its first point at the playhead. */
  pasteClipGainRide: (atPlayhead?: boolean) => void;
  copySelection: () => void;
  cutSelection: () => void;
  pasteClipboard: () => void;
  duplicateSelection: () => void;
  deleteSelection: () => void;
  splitSelectionAtPlayhead: () => void;
  crossfadeSelection: () => void;
  setDefaultCrossfade: (seconds: number) => void;

  // -- tracks
  addTrack: (kind: TrackKind) => void;
  /**
   * Measures a clip's own tempo and sets it as the clip's source tempo, so a
   * loop fits the project without anyone typing a number.
   */
  detectClipTempo: (clipId: string) => Promise<void>;
  /**
   * Finds where each beat of a clip's audio actually landed and pins every
   * one to the grid with a warp marker, so a performance that pushes and
   * drags plays straight. The clip is set to follow the tempo at the average
   * the beats make, and any markers it had are replaced.
   */
  warpClipToBeats: (clipId: string) => Promise<void>;
  /**
   * The other answer: leaves the clip exactly as played and bends the
   * project's tempo map to its beats, so a live take becomes the grid. The
   * clip stops following the tempo and loses its markers, because a clip that
   * followed the map made from it would be refitted to itself.
   */
  tempoMapFromClip: (clipId: string) => Promise<void>;
  /**
   * Renders a track by itself, measures how loud it is, and sets its fader so it
   * lands on a target. The mixing equivalent of loudness matching an export.
   */
  matchTrackLoudness: (trackId: string, targetLufs: number) => Promise<void>;
  /**
   * Renders the selected range of the selected tracks down to a single clip on a
   * new track, and clears what it replaced.
   */
  bounceRange: (options?: { keepSource?: boolean }) => Promise<void>;
  /**
   * Renders the clips inside the range on each audio track it covers — the
   * clips and only the clips — and replaces them with one clip of the render,
   * on the same track. Inserts, fader and automation stay live.
   */
  consolidateRange: () => Promise<void>;
  /** True while a bounce is rendering. */
  bouncing: boolean;
  freezeTrack: (trackId: string) => Promise<void>;
  unfreezeTrack: (trackId: string) => void;
  /** The track currently being rendered down, if any. */
  freezingTrackId: string | null;
  /** True while pitch-preserved renders are being computed for stretched clips. */
  stretching: boolean;
  /** How far through preparing renders, while `stretching`. */
  stretchProgress: { done: number; total: number } | null;
  /** The track currently being measured for loudness matching, if any. */
  matchingTrackId: string | null;
  /** Tracks with an automation pass in flight, so their strips can show it. */
  writingTracks: string[];
  /**
   * The parameters currently being written, as target keys, so a control can
   * show that it is recording without opening its lane.
   */
  writingTargets: string[];
  /** True while this exact parameter is writing. */
  isWritingTarget: (target: AutomationTarget) => boolean;

  // -- automation recording
  /**
   * Records a control's movement into its lane while the transport runs.
   *
   * `touchParameter` when the control is grabbed, `writeParameter` on every
   * change, `releaseParameter` when it is let go. The three are a unit: the
   * points collected between the first and the last become one command, so a
   * pass is one undo step and the region it covers is replaced rather than
   * merged with.
   */
  touchParameter: (target: AutomationTarget) => void;
  writeParameter: (target: AutomationTarget, normalised: number) => void;
  releaseParameter: (target: AutomationTarget) => void;
  /** True while any control on this track is writing automation. */
  isWriting: (trackId: string) => boolean;

  // -- assets
  importFiles: (files: File[], trackId: string | null, atTicks: number) => Promise<void>;
  /** Imports a recording of a room and gives it to a reverb as its impulse response. */
  loadReverbRoom: (trackId: string, effectId: string, file: File) => Promise<void>;
  /**
   * Adds a Web Audio Module from a URL as an insert. The plugin's code is
   * fetched from that URL — the one thing here that reaches out of the
   * machine — and the user is told so.
   */
  addWebAudioModule: (trackId: string, url: string) => void;
  /** Makes a Web Audio Module from a URL the track's instrument, with the same word about where its code comes from. */
  setWebAudioModuleInstrument: (trackId: string, url: string) => void;
  /**
   * Asks every hosted plugin for its whole state and keeps it on the project.
   * A plugin's interface changes its state without a command, so this runs
   * before a save or a package, and when an interface is closed.
   */
  captureWamStates: () => Promise<void>;
  importSamplerFiles: (files: File[], trackId: string) => Promise<void>;
  importMidiFiles: (files: File[], atTicks: number) => Promise<void>;
  exportMidiFile: () => Promise<void>;

  // -- recording
  refreshInputDevices: () => Promise<void>;
  toggleRecording: () => Promise<void>;

  // -- MIDI input
  enableMidi: () => Promise<void>;
  selectMidiDevice: (deviceId: string | null) => void;
  setKeyboardOctave: (octave: number) => void;
  playLiveNote: (pitch: number, velocity: number) => void;
  stopLiveNote: (pitch: number) => void;
  /** The sustain pedal: releases are held while it is down and let go when it lifts. */
  setSustainPedal: (down: boolean) => void;
  /** The pitch wheel, -1..1, bending the armed instrument by the default wheel range. */
  bendLive: (amount: number) => void;
  /** The mod wheel, 0..1, opening the armed instrument's filter. */
  modWheelLive: (amount: number) => void;

  // -- projects
  newProject: () => Promise<void>;
  saveProject: () => Promise<void>;
  openProject: (projectId: string) => Promise<void>;
  deleteProject: (projectId: string) => Promise<void>;
  duplicateProject: (projectId: string) => Promise<void>;
  refreshProjectList: () => Promise<void>;
  acceptRecovery: () => Promise<void>;
  discardRecovery: () => Promise<void>;

  // -- feedback
  notify: (toast: Omit<Toast, 'id'>) => void;
  dismissToast: (id: number) => void;
}

let toastId = 1;

const EMPTY_SELECTION: Selection = {
  clipIds: [],
  trackId: null,
  laneId: null,
  pointIds: [],
  noteIds: [],
  range: null,
};

export const useDaw = create<DawState>((set, get) => {
  const services = getServices({
    onAutosaveError: (error) =>
      get().notify({
        kind: 'warning',
        title: 'Autosave could not write to local storage',
        detail: describeError(error),
        sticky: true,
      }),
    onAutosave: (savedAt) => set({ lastAutosaveAt: savedAt }),
  });

  /**
   * The single write path for project state. Everything that changes a project
   * goes through here, so the engine and autosave can never fall out of sync
   * with what the UI is showing.
   */
  /**
   * Asks the engine to make sure everything this project needs is rendered.
   *
   * The engine decides whether there is anything to do and whether it is able to
   * do it; the store only reports it. That split is deliberate — keeping the
   * "already prepared" bookkeeping here once meant recording a project as
   * prepared before the audio context existed, so the work never happened.
   */
  const syncStretches = (project: Project): void => {
    // Asked for here rather than at startup, because this is the one place that
    // needs it and a second call site is a second thing to forget. Building it
    // costs an OfflineAudioContext, needs no user gesture, and happens once.
    services.assetPipeline();
    let started = false;
    const work = services.engine.prepareStretches(project, (done, total) => {
      started = true;
      set({ stretching: true, stretchProgress: { done, total } });
    });
    void work.catch(() => undefined).finally(() => {
      if (started) set({ stretching: false, stretchProgress: null });
    });
  };

  /**
   * Automation passes in flight, keyed by the parameter being written.
   *
   * Held outside the store because they change on every pointer event and
   * nothing renders from them; only the list of tracks currently writing is
   * state, because a strip shows that.
   */
  interface AutomationPass {
    target: AutomationTarget;
    startTicks: number;
    points: { tick: number; value: number }[];
    lastAt: number;
    latched?: boolean;
  }
  const passes = new Map<string, AutomationPass>();
  let passStartTicks = 0;

  /** Points closer together than this are not worth keeping. About 20ms at 120bpm. */
  const THIN_TICKS = 40;

  const targetKey = (target: AutomationTarget): string =>
    `${target.trackId}:${target.kind}:${target.effectId ?? ''}:${target.paramId}`;

  const currentTick = (): number => Math.max(0, Math.round(services.engine.currentTicks()));

  /**
   * A wheel to the armed instrument, in the parameter's own units — and, while
   * a take is being recorded, kept alongside the notes to be written into the
   * parameter's lane when the take lands. Thinned as the automation passes
   * are: a wheel sends far more than a lane needs, and two moves inside a tick
   * are one point.
   */
  const playLiveParam = (paramId: string, value: number): void => {
    const trackId = armedInstrumentTrack(get().project);
    if (!trackId) return;
    const instrument = services.engine.instrumentFor(trackId);
    if (!instrument) return;
    instrument.setParam(paramId, value, services.engine.context?.currentTime);

    if (get().transportState !== 'recording' || get().recordingTrackId !== trackId) return;
    const type = get().project.tracks[trackId]?.instrument?.type;
    const descriptor = type ? instrumentParamDescriptor(type, paramId) : undefined;
    if (!descriptor) return;
    const tick = currentTick();
    const normalised = normaliseParam(descriptor, value);
    const points = capturedParams.get(paramId) ?? [];
    const last = points[points.length - 1];
    if (last && last.tick === tick) last.value = normalised;
    else if (last && tick - last.tick < THIN_TICKS && Math.abs(last.value - normalised) < 0.002) return;
    else points.push({ tick, value: normalised });
    capturedParams.set(paramId, points);
  };

  /** The lane for a target, creating one if the parameter has none yet. */
  const laneFor = (target: AutomationTarget): string | null => {
    const project = get().project;
    const track = project.tracks[target.trackId];
    if (!track) return null;
    for (const laneId of track.automationLaneIds) {
      const lane = project.automation[laneId];
      if (
        lane &&
        lane.target.kind === target.kind &&
        lane.target.paramId === target.paramId &&
        lane.target.effectId === target.effectId
      ) {
        return laneId;
      }
    }
    const laneId = createId('lan');
    get().dispatch({
      type: 'automation.addLane',
      params: {
        laneId,
        trackId: target.trackId,
        kind: target.kind,
        effectId: target.effectId,
        paramId: target.paramId,
      },
    });
    if (!get().project.automation[laneId]) return null;
    // A lane that has just been performed into is shown. Recording a move and
    // seeing nothing happen is the most confusing thing this feature could do.
    if (!track.showAutomation) {
      get().dispatch({ type: 'automation.setVisible', params: { trackId: target.trackId, show: true } });
    }
    return laneId;
  };

  /** Mirrors the passes in flight into state, which is what the controls read. */
  const publishWriting = (): void => {
    const tracks = new Set<string>();
    const targets: string[] = [];
    for (const [key, pass] of passes) {
      tracks.add(pass.target.trackId);
      targets.push(key);
    }
    set({ writingTracks: [...tracks], writingTargets: targets });
  };

  const commitPass = (key: string, pass: AutomationPass): void => {
    passes.delete(key);
    publishWriting();
    if (pass.points.length === 0) return;

    const laneId = laneFor(pass.target);
    if (!laneId) return;
    const endTicks = Math.max(pass.startTicks, currentTick(), ...pass.points.map((p) => p.tick));
    get().dispatch({
      type: 'automation.writeRegion',
      params: { laneId, startTicks: pass.startTicks, endTicks, points: pass.points },
    });
    services.commands.breakCoalescing();
  };

  /** Ends every pass in flight. Called when the transport stops. */
  const commitAllPasses = (): void => {
    for (const [key, pass] of [...passes]) commitPass(key, pass);
  };

  const commit = (project: Project, options: { dirty?: boolean; autosave?: boolean } = {}): void => {
    const dirty = options.dirty ?? true;
    set({
      project,
      dirty,
      undoLabel: services.commands.undoLabel(),
      redoLabel: services.commands.redoLabel(),
    });
    services.engine.applyProject(project);
    syncStretches(project);
    if (options.autosave !== false && dirty) services.autosave.notifyChanged(project);
  };

  const clampSelection = (project: Project, selection: Selection): Selection => {
    const lane = selection.laneId ? project.automation[selection.laneId] : null;
    const livePoints = new Set(lane?.points.map((p) => p.id) ?? []);
    const clipIds = selection.clipIds.filter((id) => project.clips[id]);
    const edited = clipIds.length === 1 ? project.clips[clipIds[0]] : null;
    const liveNotes = new Set(edited?.kind === 'midi' ? edited.notes.map((n) => n.id) : []);
    const range = selection.range
      ? { ...selection.range, trackIds: selection.range.trackIds.filter((id) => project.tracks[id]) }
      : null;

    return {
      clipIds,
      trackId: selection.trackId && project.tracks[selection.trackId] ? selection.trackId : null,
      laneId: lane ? selection.laneId : null,
      pointIds: selection.pointIds.filter((id) => livePoints.has(id)),
      noteIds: selection.noteIds.filter((id) => liveNotes.has(id)),
      range: range && range.trackIds.length > 0 ? range : null,
    };
  };

  return {
    project: createEmptyProject(),
    ready: false,
    audioReady: false,
    dirty: false,
    savedProjectId: null,
    transportState: 'stopped',
    countingIn: false,
    recordingTrackId: null,
    selection: EMPTY_SELECTION,
    editTool: 'select',
    modal: 'none',
    projectList: [],
    recoveryOffer: null,
    toasts: [],
    clipboard: [],
    undoLabel: null,
    redoLabel: null,
    lastAutosaveAt: null,
    inputDevices: [],
    freezingTrackId: null,
    stretching: false,
    stretchProgress: null,
    matchingTrackId: null,
    bouncing: false,
    rangeClipboard: null,
    clipGainClipboard: null,
    rippleEditing: false,
    autoFadeOverlaps: false,
    clipGainEditing: false,
    writingTracks: [],
    writingTargets: [],
    midiStatus: MidiInput.availability(),
    pluginLatencySec: 0,
    heldBackSec: {},
    midiDevices: [],
    midiDeviceId: null,
    keyboardOctave: 4,

    // -- lifecycle ---------------------------------------------------------

    async boot() {
      services.engine.onTransportStateChange((transportState) => {
        set({ transportState });
        services.playhead.setRunning(transportState !== 'stopped');
      });
      // Where the routes are held back is shown, not only done.
      services.engine.onLatencyChange((report) => {
        set({ pluginLatencySec: report.pluginLatencySec, heldBackSec: Object.fromEntries(report.heldBackSec) });
      });
      // What a hosted plugin says about itself is kept with the project, so
      // its controls can be drawn and its lanes normalised without it.
      services.engine.onWamInfo(({ trackId, effectId, name, parameters }) => {
        const before = get().project;
        const result =
          effectId === null
            ? runCommand(before, command(MidiCommands.setInstrumentWamInfo, { trackId, name, parameters }))
            : runCommand(before, command(EffectCommands.setWamInfo, { trackId, effectId, name, parameters }));
        if (!result.changed) return;
        // Not an edit the user made: it does not dirty the project or make an
        // undo step, but the engine still gets the parameters' values.
        set({ project: result.project });
        services.engine.applyProject(result.project);
      });

      try {
        await services.storage.init();
      } catch (error) {
        get().notify({
          kind: 'warning',
          title: 'Local storage is unavailable',
          detail: `${describeError(error)} You can still work, but projects cannot be saved in this browser session.`,
          sticky: true,
        });
        set({ ready: true });
        return;
      }

      let offer: RecoveryOffer | null = null;
      try {
        offer = await findRecoveryOffer(services.storage);
      } catch {
        offer = null;
      }

      const lastId = await services.storage.getSetting<string>('lastProjectId').catch(() => null);
      let project: Project | null = null;
      if (lastId) {
        project = await services.storage.loadProject(lastId).catch(() => null);
      }
      if (!project) project = createEmptyProject();

      services.commands.clear();
      set({ ready: true, recoveryOffer: offer, savedProjectId: lastId ?? null });
      commit(project, { dirty: false, autosave: false });
      await get().refreshProjectList();
    },

    async activateAudio() {
      if (get().audioReady) return;
      try {
        const engine = await ensureAudio();
        engine.applyProject(get().project);
        set({ audioReady: true });
        // Until now there was no cache to fill and no asset pipeline to decode
        // from, so whatever the project needs has not been rendered. This is the
        // first moment it can be.
        syncStretches(get().project);

      } catch (error) {
        get().notify({
          kind: 'error',
          title: 'Audio could not start',
          detail: describeError(error),
          sticky: true,
        });
      }
    },

    // -- editing -----------------------------------------------------------

    dispatch(record) {
      const { project } = get();
      try {
        const result = services.commands.execute(project, record);
        if (!result.changed) return;
        commit(result.project);
        set({ selection: clampSelection(result.project, get().selection) });
      } catch (error) {
        get().notify({ kind: 'error', title: 'That edit could not be applied', detail: describeError(error), sticky: false });
      }
    },

    undo() {
      const next = services.commands.undo(get().project);
      if (!next) return;
      commit(next);
      set({ selection: clampSelection(next, get().selection) });
    },

    redo() {
      const next = services.commands.redo(get().project);
      if (!next) return;
      commit(next);
      set({ selection: clampSelection(next, get().selection) });
    },

    // -- view --------------------------------------------------------------

    setView(patch) {
      const project = get().project;
      const view = { ...project.view, ...patch };
      // View changes are not undoable and do not mark the project dirty for
      // autosave purposes beyond a lazy write; zooming is not editing.
      set({ project: { ...project, view } });
    },

    setSnap(snap) {
      get().dispatch(command(TransportCommands.setSnap, { snap }));
    },

    setSelection(selection) {
      set({ selection: { ...get().selection, ...selection } });
    },

    selectClips(clipIds, additive) {
      const current = get().selection.clipIds;
      const next = additive ? [...new Set([...current, ...clipIds])] : clipIds;
      const trackId = next.length > 0 ? (get().project.clips[next[next.length - 1]]?.trackId ?? null) : get().selection.trackId;
      // Selecting clips clears any automation-point selection, so Delete acts
      // on what the user just touched rather than on something off screen.
      set({ selection: { ...get().selection, clipIds: next, trackId, pointIds: [], noteIds: [] } });
    },

    setEditTool(editTool) {
      set({ editTool });
      // Leaving the range tool leaves the range behind: it is still what the
      // edit menu acts on, and clearing it would make the tools feel modal.
    },

    setRange(range) {
      set({ selection: { ...get().selection, range, clipIds: [], noteIds: [] } });
    },

    /**
     * Runs a range operation on the current range.
     *
     * Everything here needs a range, so a single check reports its absence once
     * rather than each command failing silently.
     */
    setRippleEditing(on) {
      set({ rippleEditing: on });
    },

    setAutoFadeOverlaps(on) {
      set({ autoFadeOverlaps: on });
      // Turning it on is a statement about the project as it stands, not only
      // about edits from here: the overlaps already there are what prompted it.
      if (on) get().fadeOverlaps();
    },

    setClipGainEditing(on) {
      set({ clipGainEditing: on });
    },

    applyRangeEdit(kind, allTracks = false) {
      const { selection, project } = get();
      const range = selection.range;
      if (!range || range.endTicks <= range.startTicks) {
        get().notify({
          kind: 'info',
          title: 'Select a stretch of time first',
          detail: 'Pick the Range tool and drag across the tracks you want to edit.',
          sticky: false,
        });
        return;
      }

      const params: Record<string, unknown> = {
        trackIds: range.trackIds,
        startTicks: range.startTicks,
        endTicks: range.endTicks,
        allTracks,
      };

      switch (kind) {
        case 'copy': {
          const clips = RangeCommands.copyRangeForPaste(project, {
            trackIds: range.trackIds,
            startTicks: range.startTicks,
            endTicks: range.endTicks,
          });
          const automation = RangeCommands.copyRangeAutomation(project, {
            trackIds: range.trackIds,
            startTicks: range.startTicks,
            endTicks: range.endTicks,
          });
          set({
            rangeClipboard: {
              lengthTicks: range.endTicks - range.startTicks,
              trackCount: range.trackIds.length,
              clips,
              automation,
            },
          });
          get().notify({
            kind: 'info',
            title: `Copied ${((range.endTicks - range.startTicks) / project.transport.ppq).toFixed(1)} beats`,
            detail: `${clips.length} clip${clips.length === 1 ? '' : 's'}${
              automation.length > 0 ? ` and ${automation.length} curve${automation.length === 1 ? '' : 's'}` : ''
            } across ${range.trackIds.length} track${range.trackIds.length === 1 ? '' : 's'}.`,
            sticky: false,
          });
          return;
        }
        case 'pasteReplace':
        case 'pasteInsert': {
          const clipboard = get().rangeClipboard;
          if (!clipboard) {
            get().notify({
              kind: 'info',
              title: 'Nothing on the time clipboard',
              detail: 'Mark a range and choose "Copy range" first.',
              sticky: false,
            });
            return;
          }
          // Pasted at the range's start, across as many tracks as were copied,
          // beginning with the first track of the current selection.
          const start = project.trackOrder.indexOf(range.trackIds[0]);
          const targets = project.trackOrder.slice(
            Math.max(0, start),
            Math.max(0, start) + clipboard.trackCount,
          );
          get().dispatch({
            type: RangeCommands.pasteRange.type,
            params: {
              trackIds: targets,
              startTicks: range.startTicks,
              lengthTicks: clipboard.lengthTicks,
              mode: kind === 'pasteInsert' ? 'insert' : 'replace',
              clips: clipboard.clips,
              automation: clipboard.automation,
            },
          });
          return;
        }
        case 'split':
          get().dispatch({ type: RangeCommands.splitAtRange.type, params });
          return;
        case 'delete':
          get().dispatch({ type: RangeCommands.deleteRange.type, params });
          return;
        case 'spliceOut':
          get().dispatch({ type: RangeCommands.spliceOut.type, params });
          // What follows has moved back by the length removed, so a range that
          // stayed put would no longer point at anything meaningful.
          set({ selection: { ...get().selection, range: null } });
          return;
        case 'insert':
          get().dispatch({
            type: RangeCommands.insertTime.type,
            params: {
              trackIds: range.trackIds,
              startTicks: range.startTicks,
              lengthTicks: range.endTicks - range.startTicks,
              allTracks,
            },
          });
          return;
        case 'crop':
          get().dispatch({ type: RangeCommands.cropToRange.type, params: { ...params, allTracks: true } });
          set({ selection: { ...get().selection, range: null } });
          return;
        case 'duplicate':
          get().dispatch({ type: RangeCommands.duplicateRange.type, params });
          return;
        default:
          void project;
      }
    },

    setModal(modal) {
      set({ modal });
      if (modal === 'projects') void get().refreshProjectList();
    },

    // -- transport ---------------------------------------------------------

    play() {
      void get()
        .activateAudio()
        .then(() => {
          // The integrated figure is per playback pass: it answers "how loud is
          // what I just listened to", which is only meaningful if it starts when
          // playback does.
          services.engine.resetLoudness();
          passStartTicks = get().project.transport.playheadTicks;
          services.engine.play();
          services.playhead.setRunning(true);
        });
    },

    pause() {
      // Latched passes end here as well as at stop: a performance is over when
      // the transport stops moving, whichever button did it.
      commitAllPasses();
      services.engine.pause();
      const ticks = services.engine.currentTicks();
      set({ project: { ...get().project, transport: { ...get().project.transport, playheadTicks: ticks } } });
      services.playhead.emit();
    },

    stop() {
      commitAllPasses();
      services.engine.stop();
      services.engine.seek(get().project.transport.playheadTicks);
      services.playhead.emit();
    },

    togglePlay() {
      if (get().transportState === 'stopped') get().play();
      else get().pause();
    },

    seekTicks(ticks) {
      const clamped = Math.max(0, Math.round(ticks));
      services.engine.seek(clamped);
      set({ project: { ...get().project, transport: { ...get().project.transport, playheadTicks: clamped } } });
      services.playhead.emit();
    },

    toggleLoop() {
      get().dispatch(command(TransportCommands.setLoopEnabled, { enabled: !get().project.transport.loopEnabled }));
    },

    loopFromRange() {
      const { range } = get().selection;
      if (!range || range.endTicks - range.startTicks < 1) return;
      // Marking a stretch to loop is asking to hear it: the loop is switched on
      // as well as moved, or the button would appear to do nothing.
      get().dispatch(
        command(TransportCommands.setLoopRegion, {
          startTicks: Math.round(range.startTicks),
          endTicks: Math.round(range.endTicks),
          enabled: true,
        }),
      );
    },

    rangeFromLoop() {
      const { project } = get();
      const { loopStartTicks, loopEndTicks } = project.transport;
      if (loopEndTicks - loopStartTicks < 1) return;
      // A loop is project-wide, so the range it becomes covers every track;
      // narrowing it to one is a drag on the range tool afterwards.
      const trackIds = project.trackOrder.filter((id) => project.tracks[id]);
      if (trackIds.length === 0) return;
      get().setRange({ trackIds, startTicks: loopStartTicks, endTicks: loopEndTicks });
      get().setEditTool('range');
    },

    toggleMetronome() {
      get().dispatch(command(TransportCommands.setMetronome, { enabled: !get().project.transport.metronomeEnabled }));
    },

    setTempo(bpm, tick = 0) {
      get().dispatch(command(TransportCommands.setTempo, { tick, bpm }));
    },

    // -- clipboard ---------------------------------------------------------

    copyClipGainRide() {
      const { project, selection } = get();
      const clip = project.clips[selection.clipIds[0] ?? ''];
      if (clip?.kind !== 'audio') return;

      const clipboard = copyClipGain(project, clip);
      if (!clipboard) {
        get().notify({
          kind: 'info',
          title: 'That clip has no ride to copy',
          detail: 'Draw one with the Clip gain tool first.',
          sticky: false,
        });
        return;
      }
      set({ clipGainClipboard: clipboard });
    },

    pasteClipGainRide(atPlayhead = false) {
      const { clipGainClipboard, project, selection } = get();
      if (!clipGainClipboard) {
        get().notify({
          kind: 'info',
          title: 'No ride has been copied',
          detail: 'Select a clip carrying one and choose "Copy ride".',
          sticky: false,
        });
        return;
      }
      const clipIds = selection.clipIds.filter((id) => project.clips[id]?.kind === 'audio');
      if (clipIds.length === 0) return;
      get().dispatch(
        command(ClipGainCommands.pasteClipGainEnvelope, {
          clipIds,
          points: clipGainClipboard.points,
          atTick: atPlayhead ? project.transport.playheadTicks : null,
        }),
      );
    },

    copySelection() {
      const { project, selection } = get();
      const clips = selection.clipIds.map((id) => project.clips[id]).filter((c): c is Clip => Boolean(c));
      if (clips.length > 0) set({ clipboard: clips.map((c) => cloneClip(c)) });
    },

    cutSelection() {
      get().copySelection();
      get().deleteSelection();
    },

    pasteClipboard() {
      const { clipboard, project, selection } = get();
      if (clipboard.length === 0) return;
      get().dispatch(
        command(ClipCommands.pasteClips, {
          clips: clipboard,
          atTick: project.transport.playheadTicks,
          targetTrackId: selection.trackId,
        }),
      );
    },

    duplicateSelection() {
      const { selection } = get();
      if (selection.range) {
        get().applyRangeEdit('duplicate');
        return;
      }
      if (selection.clipIds.length === 0) return;
      get().dispatch(command(ClipCommands.duplicateClips, { clipIds: selection.clipIds, offsetTicks: null }));
    },

    deleteSelection() {
      const { selection } = get();
      // Delete acts on whatever the last gesture selected: a stretch of time
      // if one is marked, notes if the user was in the piano roll, automation
      // points if they were in a lane, and clips otherwise.
      if (selection.range) {
        get().applyRangeEdit('delete');
        return;
      }
      if (selection.noteIds.length > 0 && selection.clipIds.length === 1) {
        get().dispatch({
          type: 'midi.deleteNotes',
          params: { clipId: selection.clipIds[0], noteIds: selection.noteIds },
        });
        set({ selection: { ...get().selection, noteIds: [] } });
        return;
      }
      if (selection.laneId && selection.pointIds.length > 0) {
        get().dispatch({
          type: 'automation.deletePoints',
          params: { laneId: selection.laneId, pointIds: selection.pointIds },
        });
        set({ selection: { ...get().selection, pointIds: [] } });
        return;
      }
      if (selection.clipIds.length === 0) return;
      get().dispatch(
        command(ClipCommands.deleteClips, { clipIds: selection.clipIds, ripple: get().rippleEditing }),
      );
      set({ selection: { ...selection, clipIds: [] } });
    },

    splitSelectionAtPlayhead() {
      const { project, selection } = get();
      const tick = project.transport.playheadTicks;
      const targets =
        selection.clipIds.length > 0
          ? selection.clipIds
          : Object.values(project.clips)
              .filter((clip) => clip.startTicks < tick && clipEndTicks(project, clip) > tick)
              .map((clip) => clip.id);
      if (targets.length === 0) return;
      get().dispatch(command(ClipCommands.splitClips, { clipIds: targets, tick }));
    },

    /**
     * Crossfades the join the user means: the two clips they have selected, or
     * failing that the edit nearest the playhead on the selected track. Asking
     * them to select exactly two clips before every crossfade would be busywork
     * when the playhead is already sitting on the edit.
     */
    crossfadeSelection() {
      const { project, selection } = get();
      const audioPair = selection.clipIds
        .map((id) => project.clips[id])
        .filter((clip) => clip?.kind === 'audio');

      if (audioPair.length === 2) {
        get().dispatch({
          type: CrossfadeCommands.crossfadeClips.type,
          params: { clipIds: audioPair.map((clip) => clip.id) },
        });
        return;
      }

      const trackId = selection.trackId ?? audioPair[0]?.trackId ?? null;
      if (!trackId) {
        get().notify({
          kind: 'info',
          title: 'Nothing to crossfade',
          detail: 'Select two clips that meet, or put the playhead on an edit.',
          sticky: false,
        });
        return;
      }

      const takeLane = audioPair[0]?.takeLane ?? 0;
      const before = get().project;
      get().dispatch({
        type: CrossfadeCommands.crossfadeClips.type,
        params: { trackId, tick: project.transport.playheadTicks, takeLane },
      });

      if (get().project === before) {
        get().notify({
          kind: 'info',
          title: 'No edit to crossfade there',
          detail: 'Crossfades join two clips that meet; move the playhead onto one.',
          sticky: false,
        });
      }
    },

    setDefaultCrossfade(seconds) {
      get().dispatch(command(CrossfadeCommands.setDefaultCrossfade, { seconds }));
    },

    // -- tracks ------------------------------------------------------------

    addTrack(kind) {
      get().dispatch({ type: TrackCommands.addTrack.type, params: { kind } });
    },

    /**
     * Renders a track down to audio and plays that instead.
     *
     * The render, the asset write and the state change are one undo step: a
     * half-frozen track — audio stored but the track still live, or the other
     * way round — is not a state anyone should be able to reach.
     */
    async detectClipTempo(clipId) {
      await get().activateAudio();
      const assets = services.assets();
      const clip = get().project.clips[clipId];
      if (!assets || !clip || clip.kind !== 'audio') return;

      const buffer = await assets.getBuffer(clip.assetId);
      if (!buffer) {
        get().notify({ kind: 'error', title: 'That clip has no audio to analyse', sticky: false });
        return;
      }

      // Only the clip's own region is analysed, not the whole asset: a clip is
      // often one bar taken out of a longer take, and the rest of the take may
      // be at a different tempo or not be music at all.
      const from = Math.max(0, Math.floor(clip.offsetSec * buffer.sampleRate));
      const to = Math.min(buffer.length, Math.ceil((clip.offsetSec + clip.durationSec) * buffer.sampleRate));
      const channels: Float32Array[] = [];
      for (let c = 0; c < buffer.numberOfChannels; c++) {
        channels.push(buffer.getChannelData(c).subarray(from, to));
      }

      const found = detectTempo(channels, buffer.sampleRate, { fitToLengthSec: clip.durationSec });
      if (!found || found.confidence < 0.2) {
        get().notify({
          kind: 'warning',
          title: 'No steady pulse found in that clip',
          detail: 'Set the source tempo by hand, or try a selection with a clearer beat in it.',
          sticky: false,
        });
        return;
      }

      get().dispatch(
        command(ClipCommands.setClipWarp, { clipIds: [clipId], follow: true, sourceBpm: found.bpm }),
      );
      get().notify({
        kind: 'info',
        title: `Detected ${found.bpm.toFixed(2)} BPM`,
        detail: found.fittedToLength
          ? 'Fitted so the clip is a whole number of beats.'
          : `Confidence ${(found.confidence * 100).toFixed(0)}%.`,
        sticky: false,
      });
    },

    async warpClipToBeats(clipId) {
      await get().activateAudio();
      const assets = services.assets();
      const clip = get().project.clips[clipId];
      if (!assets || !clip || clip.kind !== 'audio') return;

      const buffer = await assets.getBuffer(clip.assetId);
      if (!buffer) {
        get().notify({ kind: 'error', title: 'That clip has no audio to analyse', sticky: false });
        return;
      }
      const from = Math.max(0, Math.floor(clip.offsetSec * buffer.sampleRate));
      const to = Math.min(buffer.length, Math.ceil((clip.offsetSec + clip.durationSec) * buffer.sampleRate));
      const channels: Float32Array[] = [];
      for (let c = 0; c < buffer.numberOfChannels; c++) channels.push(buffer.getChannelData(c).subarray(from, to));

      const found = detectBeats(channels, buffer.sampleRate);
      const pinned = found && found.confidence >= 0.2 ? warpMarkersForBeats(found, newWarpMarkerId) : null;
      if (!found || !pinned) {
        get().notify({
          kind: 'warning',
          title: 'No beats to pin in that clip',
          detail: 'Nothing with a clear pulse was found. Try a selection with a steadier beat in it.',
          sticky: false,
        });
        return;
      }

      // One undo step: the tempo, the old markers gone, the new ones in.
      const before = get().project;
      let next = runCommand(before, command(ClipCommands.setClipWarp, { clipIds: [clipId], follow: true, sourceBpm: pinned.sourceBpm })).project;
      next = runCommand(next, command(ClipCommands.clearWarpMarkers, { clipIds: [clipId] })).project;
      let added = 0;
      for (const marker of pinned.markers) {
        const result = runCommand(next, {
          type: ClipCommands.addWarpMarker.type,
          params: { clipId, markerId: marker.id, sourceSec: marker.sourceSec, beat: marker.beat },
        });
        if (result.changed) added += 1;
        next = result.project;
      }
      services.commands.pushCheckpoint(before, 'Warp to beats');
      commit(next);
      get().notify({
        kind: 'info',
        title: `Pinned ${added} beats at ${pinned.sourceBpm.toFixed(2)} BPM`,
        detail: `${found.beatSec.length} beats were found; each one is now on the grid.`,
        sticky: false,
      });
    },

    async tempoMapFromClip(clipId) {
      await get().activateAudio();
      const assets = services.assets();
      const clip = get().project.clips[clipId];
      if (!assets || !clip || clip.kind !== 'audio') return;

      const buffer = await assets.getBuffer(clip.assetId);
      if (!buffer) {
        get().notify({ kind: 'error', title: 'That clip has no audio to analyse', sticky: false });
        return;
      }
      const from = Math.max(0, Math.floor(clip.offsetSec * buffer.sampleRate));
      const to = Math.min(buffer.length, Math.ceil((clip.offsetSec + clip.durationSec) * buffer.sampleRate));
      const channels: Float32Array[] = [];
      for (let c = 0; c < buffer.numberOfChannels; c++) channels.push(buffer.getChannelData(c).subarray(from, to));

      const found = detectBeats(channels, buffer.sampleRate);
      const numbered = found && found.confidence >= 0.2 ? numberBeats(found) : null;
      if (!found || !numbered) {
        get().notify({
          kind: 'warning',
          title: 'No beats to build a tempo map from',
          detail: 'Nothing with a clear pulse was found. Try a clip with a steadier beat in it.',
          sticky: false,
        });
        return;
      }

      const before = get().project;
      // The clip plays as recorded: no following, no markers, its own rate.
      let next = runCommand(before, command(ClipCommands.clearWarpMarkers, { clipIds: [clipId] })).project;
      next = runCommand(next, command(ClipCommands.setClipWarp, { clipIds: [clipId], follow: false, sourceBpm: null })).project;

      let beats = found.beatSec.map((sec, i) => ({ sec, number: numbered.numbers[i] }));
      let result = tempoMapFromBeats(next.transport.tempoMap, next.transport.ppq, clip.startTicks, beats);
      let startTicks = clip.startTicks;
      if (result && result.moveEarlierSec > 0) {
        // The first beat came in inside the first half-beat: the clip moves
        // earlier by that much so its first beat is its start, and the beats
        // are re-read from there.
        const map = tempoMapOf(next);
        const delta = Math.round(secondsSpanToTicks(map, clip.startTicks, -result.moveEarlierSec));
        startTicks = Math.max(0, clip.startTicks + delta);
        next = runCommand(next, command(ClipCommands.moveClips, { clipIds: [clipId], deltaTicks: startTicks - clip.startTicks, targetTrackIds: null })).project;
        const shift = found.beatSec[0];
        beats = found.beatSec.map((sec, i) => ({ sec: sec - shift, number: numbered.numbers[i] }));
        result = tempoMapFromBeats(next.transport.tempoMap, next.transport.ppq, startTicks, beats);
      }
      if (!result) return;
      next = runCommand(next, command(TransportCommands.setTempoMap, { tempoMap: result.tempoMap })).project;

      services.commands.pushCheckpoint(before, 'Tempo map from clip');
      commit(next);
      get().notify({
        kind: 'info',
        title: `Tempo map follows ${clip.name}`,
        detail: `${found.beatSec.length} beats set the grid; the clip plays exactly as recorded.`,
        sticky: false,
      });
    },

    touchParameter(target) {
      const state = get();
      const track = state.project.tracks[target.trackId];
      if (!track || track.automationMode === 'read') return;
      if (state.transportState === 'stopped') return;

      const key = targetKey(target);
      if (passes.has(key)) return;
      // `write` begins where the transport did rather than where the control was
      // grabbed, which is the whole difference between it and `latch`.
      const from =
        track.automationMode === 'write' ? Math.min(passStartTicks, get().project.transport.playheadTicks) : currentTick();
      passes.set(key, { target, startTicks: from, points: [], lastAt: -Infinity });
      publishWriting();
    },

    writeParameter(target, normalised) {
      const pass = passes.get(targetKey(target));
      if (!pass) return;
      const tick = currentTick();
      const value = Math.min(1, Math.max(0, normalised));
      const last = pass.points[pass.points.length - 1];

      // Thinned as it is collected. A pointer emits far more events than a lane
      // needs, and a curve made of five hundred points a second is unreadable,
      // unselectable and slow — while being no more accurate than one point
      // every twenty milliseconds, which is faster than any fader is moved.
      if (last && tick - pass.lastAt < THIN_TICKS && Math.abs(last.value - value) < 0.002) return;
      if (last && last.tick === tick) {
        last.value = value;
        return;
      }
      pass.points.push({ tick, value });
      pass.lastAt = tick;
    },

    releaseParameter(target) {
      const key = targetKey(target);
      const pass = passes.get(key);
      if (!pass) return;
      const track = get().project.tracks[target.trackId];

      // Touch ends with the control; latch and write run on until the transport
      // stops, so the grip is released but the pass is not.
      if (track && (track.automationMode === 'latch' || track.automationMode === 'write')) {
        pass.latched = true;
        return;
      }
      commitPass(key, pass);
    },

    isWriting(trackId) {
      return get().writingTracks.includes(trackId);
    },

    isWritingTarget(target) {
      return get().writingTargets.includes(targetKey(target));
    },

    async matchTrackLoudness(trackId, targetLufs) {
      await get().activateAudio();
      const assets = services.assets();
      const project = get().project;
      const track = project.tracks[trackId];
      if (!assets || !track || track.kind === 'master') return;

      set({ matchingTrackId: trackId });
      try {
        // Rendered in isolation and *pre-fader*, so what is measured is the
        // material itself rather than the level it is currently set to. The
        // fader then becomes the answer instead of part of the question.
        const buffer = await renderProject(project, assets, {
          isolateTrackId: trackId,
          channelCount: trackChannelCount(project, trackId),
          tailSec: 0,
        });
        const measured = measureLoudness(bufferToChannels(buffer), buffer.sampleRate);
        if (!Number.isFinite(measured.integratedLufs)) {
          get().notify({
            kind: 'warning',
            title: `"${track.name}" is silent`,
            detail: 'There is nothing on it to measure.',
            sticky: false,
          });
          return;
        }

        const wanted = Math.pow(10, (targetLufs - measured.integratedLufs) / 20);
        const volume = Math.min(4, Math.max(0, wanted));
        get().dispatch(command(TrackCommands.setTrackVolume, { trackId, volume }));
        services.commands.breakCoalescing();

        get().notify({
          kind: 'info',
          title: `"${track.name}" matched to ${targetLufs} LUFS`,
          detail:
            volume === wanted
              ? `It measured ${measured.integratedLufs.toFixed(1)} LUFS; the fader moved to ${(20 * Math.log10(Math.max(1e-6, volume))).toFixed(1)} dB.`
              : 'The fader reached its limit before the target did.',
          sticky: false,
        });
      } catch (error) {
        get().notify({
          kind: 'error',
          title: 'That track could not be measured',
          detail: describeError(error),
          sticky: false,
        });
      } finally {
        set({ matchingTrackId: null });
      }
    },

    async consolidateRange() {
      await get().activateAudio();
      const assets = services.assets();
      const state = get();
      const range = state.selection.range;
      if (!assets || !range || range.endTicks <= range.startTicks) {
        get().notify({
          kind: 'info',
          title: 'Nothing selected to consolidate',
          detail: 'Choose the Range tool and drag across the clips you want rendered down.',
          sticky: false,
        });
        return;
      }
      if (consolidatableTracks(state.project, range).length === 0) {
        get().notify({
          kind: 'info',
          title: 'Nothing to consolidate',
          detail: 'Consolidating renders audio clips, and no audio clip lies inside the range.',
          sticky: false,
        });
        return;
      }

      set({ bouncing: true });
      try {
        const before = get().project;
        const result = await consolidateRange(before, assets, range, services.engine.context?.sampleRate ?? 48000);
        if (!result) return;
        const label =
          result.trackIds.length === 1 ? (before.tracks[result.trackIds[0]]?.name ?? 'track') : `${result.trackIds.length} tracks`;
        services.commands.pushCheckpoint(before, `Consolidate ${label}`);
        commit(result.project);
        set({ selection: { ...get().selection, range: null, clipIds: result.clipIds, trackId: result.trackIds[0] } });
        get().notify({
          kind: 'success',
          title: `Consolidated ${label}`,
          detail: `${result.durationSec.toFixed(1)}s of clips rendered to one clip per track; inserts, fader and automation are untouched.`,
          sticky: false,
        });
      } catch (error) {
        get().notify({
          kind: 'error',
          title: 'That range could not be consolidated',
          detail: describeError(error),
          sticky: true,
        });
      } finally {
        set({ bouncing: false });
      }
    },

    async bounceRange(options = {}) {
      await get().activateAudio();
      const assets = services.assets();
      const state = get();
      const range = state.selection.range;
      if (!assets || !range || range.endTicks <= range.startTicks) {
        get().notify({
          kind: 'info',
          title: 'Nothing selected to bounce',
          detail: 'Choose the Range tool and drag across the part you want rendered.',
          sticky: false,
        });
        return;
      }

      const project = state.project;
      const trackIds = range.trackIds.filter((id) => {
        const track = project.tracks[id];
        return track && track.kind !== 'master';
      });
      if (trackIds.length === 0) return;

      set({ bouncing: true });
      try {
        const buffer = await renderProject(project, assets, {
          startTicks: range.startTicks,
          endTicks: range.endTicks,
          sampleRate: services.engine.context?.sampleRate ?? 48000,
          // No tail: a bounce is a piece of the arrangement and has to be the
          // length of the range, or it would overlap whatever follows it.
          tailSec: 0,
          bounceTrackIds: trackIds,
        });

        const channels = bufferToChannels(buffer);
        const wav = new Blob([encodeWav(channels, buffer.sampleRate, { bitDepth: 24 }) as BlobPart], {
          type: 'audio/wav',
        });
        const names = trackIds.map((id) => project.tracks[id]?.name ?? 'track');
        const label = names.length === 1 ? names[0] : `${names.length} tracks`;
        const result = await assets.registerRecording(buffer, wav, `${label} (bounce)`);

        const before = get().project;
        let next = runCommand(before, command(TransportCommands.registerAsset, { asset: result.asset })).project;

        // A new track rather than the source: the bounce already has the faders,
        // inserts and automation of the tracks it came from baked into it, so
        // putting it back where it came from would apply all of that a second
        // time.
        const trackId = createId('trk');
        next = runCommand(next, { type: 'track.add', params: { kind: 'audio', trackId, name: `${label} bounce` } })
          .project;
        next = runCommand(next, {
          type: 'clip.addAudio',
          params: {
            clipId: createId('clp'),
            trackId,
            assetId: result.asset.id,
            name: `${label} bounce`,
            startTicks: range.startTicks,
            offsetSec: 0,
            durationSec: buffer.duration,
          },
        }).project;

        if (!options.keepSource) {
          next = runCommand(next, {
            type: 'range.delete',
            params: { trackIds, startTicks: range.startTicks, endTicks: range.endTicks },
          }).project;
        }

        services.commands.pushCheckpoint(before, `Bounce ${label}`);
        commit(next);
        set({ selection: { ...get().selection, range: null, trackId, clipIds: [] } });
        get().notify({
          kind: 'success',
          title: `Bounced ${label}`,
          detail: options.keepSource
            ? `${buffer.duration.toFixed(1)}s rendered onto a new track.`
            : `${buffer.duration.toFixed(1)}s rendered onto a new track; the range was cleared from the source.`,
          sticky: false,
        });
      } catch (error) {
        get().notify({
          kind: 'error',
          title: 'That range could not be bounced',
          detail: describeError(error),
          sticky: true,
        });
      } finally {
        set({ bouncing: false });
      }
    },

    async freezeTrack(trackId) {
      await get().activateAudio();
      const assets = services.assets();
      const project = get().project;
      const track = project.tracks[trackId];
      if (!assets || !track || track.kind === 'master') return;

      const range = freezeRange(project, trackId);
      if (range.endTicks <= range.startTicks) {
        get().notify({
          kind: 'info',
          title: `"${track.name}" has nothing to freeze`,
          detail: 'Add some material to the track first.',
          sticky: false,
        });
        return;
      }

      set({ freezingTrackId: trackId });
      try {
        const buffer = await renderProject(project, assets, {
          startTicks: range.startTicks,
          endTicks: range.endTicks,
          sampleRate: services.engine.context?.sampleRate ?? 48000,
          // The tail carries reverb and delay past the last note; without it a
          // frozen track would end more abruptly than the live one.
          tailSec: 2,
          isolateTrackId: trackId,
          // Rendering at the track's own width keeps the pan law the same when
          // the frozen file plays back through it.
          channelCount: trackChannelCount(project, trackId),
        });

        const channels = bufferToChannels(buffer);
        const wav = new Blob([encodeWav(channels, buffer.sampleRate, { bitDepth: 24 }) as BlobPart], {
          type: 'audio/wav',
        });
        const result = await assets.registerRecording(buffer, wav, `${track.name} (frozen)`);

        const before = get().project;
        let next = runCommand(before, command(TransportCommands.registerAsset, { asset: result.asset })).project;
        next = runCommand(next, {
          type: FreezeCommands.freezeTrack.type,
          params: {
            trackId,
            assetId: result.asset.id,
            startTicks: range.startTicks,
            lengthTicks: range.endTicks - range.startTicks,
            signature: freezeSignature(before, trackId),
          },
        }).project;

        services.commands.pushCheckpoint(before, `Freeze "${track.name}"`);
        commit(next);
        get().notify({
          kind: 'success',
          title: `"${track.name}" frozen`,
          detail: `${buffer.duration.toFixed(1)}s rendered. Its instrument and effects are no longer running.`,
          sticky: false,
        });
      } catch (error) {
        get().notify({
          kind: 'error',
          title: `"${track.name}" could not be frozen`,
          detail: describeError(error),
          sticky: true,
        });
      } finally {
        set({ freezingTrackId: null });
      }
    },

    unfreezeTrack(trackId) {
      get().dispatch(command(FreezeCommands.unfreezeTrack, { trackId }));
    },

    // -- assets ------------------------------------------------------------

    /**
     * Import is not a single command because the expensive part — decoding and
     * peak generation — happens outside the domain. The project edit that
     * follows is applied as one undoable checkpoint, so "Undo" after dropping
     * six files removes six clips in one step, which is what a user means.
     */
    async importFiles(files, trackId, atTicks) {
      if (files.length === 0) return;
      await get().activateAudio();
      const assets = services.assets();
      if (!assets) {
        get().notify({ kind: 'error', title: 'Audio is not ready yet', detail: 'Try again in a moment.', sticky: false });
        return;
      }

      const before = get().project;
      let project = before;
      let placeAt = atTicks;
      let imported = 0;
      const failures: string[] = [];

      for (const file of files) {
        try {
          const result = await assets.importFile(file, file.name);

          let targetTrackId = trackId;
          if (!targetTrackId) {
            const empty = project.trackOrder.find(
              (id) => project.tracks[id]?.kind === 'audio' && project.tracks[id].clipIds.length === 0,
            );
            if (empty) targetTrackId = empty;
          }
          if (!targetTrackId) {
            project = runCommand(project, { type: TrackCommands.addTrack.type, params: { kind: 'audio' } }).project;
            targetTrackId = project.trackOrder[project.trackOrder.length - 1];
          }

          project = runCommand(project, command(TransportCommands.registerAsset, { asset: result.asset })).project;
          project = runCommand(project, {
            type: ClipCommands.addAudioClip.type,
            params: {
              trackId: targetTrackId,
              assetId: result.asset.id,
              name: result.asset.name,
              startTicks: placeAt,
              offsetSec: 0,
              durationSec: result.buffer.duration,
            },
          }).project;

          // Lay multiple dropped files end to end rather than on top of each other.
          const map = tempoMapOf(project);
          placeAt = secondsToTicks(map, ticksToSeconds(map, placeAt) + result.buffer.duration);
          imported++;
        } catch (error) {
          failures.push(error instanceof AudioImportError ? error.message : `${file.name}: ${describeError(error)}`);
        }
      }

      if (imported > 0) {
        services.commands.pushCheckpoint(before, imported === 1 ? 'Import audio' : `Import ${imported} files`);
        commit(project);
      }

      if (failures.length > 0) {
        get().notify({
          kind: failures.length === files.length ? 'error' : 'warning',
          title: failures.length === 1 ? 'A file could not be imported' : `${failures.length} files could not be imported`,
          detail: failures.slice(0, 3).join(' · '),
          sticky: true,
        });
      } else if (imported > 0) {
        get().notify({
          kind: 'success',
          title: imported === 1 ? 'Audio imported' : `${imported} files imported`,
          sticky: false,
        });
      }
    },

    /**
     * Loads audio files into a sampler as mapped zones.
     *
     * Root notes come from the file names where they carry one — sample
     * libraries name files `Piano_C3.wav` almost universally, and reading that
     * is the difference between dropping in a folder and having a playable
     * instrument, versus setting sixty root notes by hand. Everything else is
     * laid out in order from C3. The whole drop is one undo step.
     */
    addWebAudioModule(trackId, url) {
      const trimmed = url.trim();
      if (!isWamUrl(trimmed)) {
        get().notify({
          kind: 'warning',
          title: 'That is not a plugin URL',
          detail: 'A Web Audio Module is loaded from an http or https address that serves its module.',
          sticky: false,
        });
        return;
      }
      const effectId = newEffectId();
      const before = get().project;
      const result = runCommand(before, command(EffectCommands.addEffect, { trackId, effectId, type: 'wam', index: null, wamUrl: trimmed }));
      if (!result.changed) return;
      services.commands.pushCheckpoint(before, 'Add a Web Audio Module');
      commit(result.project);
      let origin = trimmed;
      try {
        origin = new URL(trimmed).origin;
      } catch {
        /* checked above */
      }
      get().notify({
        kind: 'info',
        title: 'Loading a plugin from the web',
        detail: `Its code comes from ${origin}. Your audio still stays on this computer: the plugin runs here, and nothing is sent to it.`,
        sticky: false,
      });
    },

    async captureWamStates() {
      const captured = await services.engine.captureWamStates();
      if (captured.length === 0) return;
      let project = get().project;
      for (const { trackId, effectId, state } of captured) {
        const record =
          effectId === null
            ? command(MidiCommands.setInstrumentWamState, { trackId, state })
            : command(EffectCommands.setWamState, { trackId, effectId, state });
        project = runCommand(project, record).project;
      }
      if (project === get().project) return;
      // Not an edit the user made: no undo step, and the engine already
      // holds this state — it is where it came from.
      set({ project });
      services.autosave.notifyChanged(project);
    },

    setWebAudioModuleInstrument(trackId, url) {
      const trimmed = url.trim();
      if (!isWamUrl(trimmed)) {
        get().notify({
          kind: 'warning',
          title: 'That is not a plugin URL',
          detail: 'A Web Audio Module is loaded from an http or https address that serves its module.',
          sticky: false,
        });
        return;
      }
      const before = get().project;
      const result = runCommand(before, command(MidiCommands.setInstrument, { trackId, instrumentType: 'wam', wamUrl: trimmed }));
      if (!result.changed) return;
      services.commands.pushCheckpoint(before, 'Load a Web Audio Module');
      commit(result.project);
      let origin = trimmed;
      try {
        origin = new URL(trimmed).origin;
      } catch {
        /* checked above */
      }
      get().notify({
        kind: 'info',
        title: 'Loading a plugin instrument from the web',
        detail: `Its code comes from ${origin}. Your audio still stays on this computer: the plugin runs here, and nothing is sent to it.`,
        sticky: false,
      });
    },

    async loadReverbRoom(trackId, effectId, file) {
      await get().activateAudio();
      const assets = services.assets();
      if (!assets) return;
      try {
        const result = await assets.importFile(file, file.name);
        const before = get().project;
        let next = runCommand(before, command(TransportCommands.registerAsset, { asset: result.asset })).project;
        next = runCommand(next, command(EffectCommands.setEffectImpulse, { trackId, effectId, assetId: result.asset.id })).project;
        services.commands.pushCheckpoint(before, 'Load a room');
        commit(next);
        get().notify({
          kind: 'info',
          title: `Reverb plays ${result.asset.name}`,
          detail: `${result.buffer.duration.toFixed(2)}s of room; size and damping no longer apply.`,
          sticky: false,
        });
      } catch (error) {
        get().notify({ kind: 'error', title: 'That room could not be loaded', detail: describeError(error), sticky: true });
      }
    },

    async importSamplerFiles(files, trackId) {
      if (files.length === 0) return;
      await get().activateAudio();
      const assets = services.assets();
      const track = get().project.tracks[trackId];
      if (!assets || track?.instrument?.type !== 'sampler') return;

      const before = get().project;
      let project = before;
      const roots = assignRootPitches(files.map((file) => file.name));
      const failures: string[] = [];
      let added = 0;

      for (const [index, file] of files.entries()) {
        try {
          const result = await assets.importFile(file, file.name);
          project = runCommand(project, command(TransportCommands.registerAsset, { asset: result.asset })).project;
          project = runCommand(project, {
            type: SamplerCommands.addSamplerZone.type,
            params: {
              trackId,
              assetId: result.asset.id,
              name: result.asset.name,
              rootPitch: roots[index],
              offsetSec: 0,
              durationSec: result.buffer.duration,
              autoRange: true,
            },
          }).project;
          added++;
        } catch (error) {
          failures.push(error instanceof AudioImportError ? error.message : `${file.name}: ${describeError(error)}`);
        }
      }

      if (added > 0) {
        services.commands.pushCheckpoint(before, added === 1 ? 'Add sample' : `Add ${added} samples`);
        commit(project);
        get().notify({
          kind: 'success',
          title: added === 1 ? 'Sample mapped' : `${added} samples mapped`,
          detail: 'Root notes were read from the file names where possible.',
          sticky: false,
        });
      }

      if (failures.length > 0) {
        get().notify({
          kind: added > 0 ? 'warning' : 'error',
          title: failures.length === 1 ? 'A sample could not be loaded' : `${failures.length} samples could not be loaded`,
          detail: failures.slice(0, 3).join(' · '),
          sticky: true,
        });
      }
    },

    /**
     * Imports Standard MIDI Files as instrument tracks.
     *
     * The whole file becomes one undo step, because "undo the import" is what a
     * user means after dropping in a file that turned out to be the wrong one.
     */
    async importMidiFiles(files, atTicks) {
      if (files.length === 0) return;
      await get().activateAudio();

      const before = get().project;
      let project = before;
      const warnings: string[] = [];
      const failures: string[] = [];
      const clipIds: string[] = [];
      let tracks = 0;
      let notes = 0;

      for (const file of files) {
        try {
          const parsed = decodeMidiFile(await file.arrayBuffer());
          const plan = planMidiImport(parsed, project, { atTicks, fallbackName: stripExtension(file.name) });
          for (const record of plan.commands) project = runCommand(project, record).project;
          warnings.push(...plan.warnings);
          clipIds.push(...plan.clipIds);
          tracks += plan.trackCount;
          notes += plan.noteCount;
        } catch (error) {
          failures.push(error instanceof MidiFileError ? error.message : `${file.name}: ${describeError(error)}`);
        }
      }

      if (tracks > 0) {
        services.commands.pushCheckpoint(before, files.length === 1 ? 'Import MIDI file' : 'Import MIDI files');
        commit(project);
        set({ selection: { ...get().selection, clipIds: clipIds.slice(0, 1), noteIds: [] } });
        get().notify({
          kind: warnings.length > 0 ? 'warning' : 'success',
          title: `Imported ${tracks} part${tracks === 1 ? '' : 's'}, ${notes} notes`,
          detail: warnings.slice(0, 2).join(' · ') || undefined,
          sticky: warnings.length > 0,
        });
      }

      if (failures.length > 0) {
        get().notify({
          kind: tracks > 0 ? 'warning' : 'error',
          title: failures.length === 1 ? 'A MIDI file could not be imported' : `${failures.length} MIDI files failed`,
          detail: failures.slice(0, 2).join(' · '),
          sticky: true,
        });
      }
    },

    /** Writes the project's MIDI parts out as a Standard MIDI File. */
    async exportMidiFile() {
      const project = get().project;
      const { bytes, trackCount, noteCount, wheelEventCount } = encodeProjectAsMidi(project);

      if (trackCount === 0) {
        get().notify({
          kind: 'warning',
          title: 'There is no MIDI to export',
          detail: 'Add an instrument track with some notes first.',
          sticky: false,
        });
        return;
      }

      const name = `${project.meta.name.replace(/[\\/:*?"<>|]/g, '-')}.mid`;
      const blob = new Blob([bytes as BlobPart], { type: 'audio/midi' });
      try {
        const target = await pickSaveTarget(name, 'MIDI file', { 'audio/midi': ['.mid'] });
        await writeFile(target, blob);
      } catch (error) {
        if (error instanceof FileCancelled) return;
        downloadBlob(blob, name);
      }

      get().notify({
        kind: 'success',
        title: 'MIDI exported',
        detail:
          `${trackCount} part(s), ${noteCount} notes` +
          (wheelEventCount > 0 ? `, and the wheels as ${wheelEventCount} bend and controller events.` : '.'),
        sticky: false,
      });
    },

    // -- recording ---------------------------------------------------------

    async refreshInputDevices() {
      set({ inputDevices: await Recorder.listInputs() });
    },

    /**
     * Recording is armed per track. The count-in, if any, runs as ordinary
     * playback before capture starts, so the performer hears the click in
     * tempo and the take lands exactly on the bar it should.
     */
    async toggleRecording() {
      const state = get();
      if (state.transportState === 'recording') {
        if (armedInstrumentTrack(state.project) && !services.recorder()?.isOpen) {
          finishMidiRecording(set, get, services);
          return;
        }
        await finishRecording(set, get, services);
        return;
      }

      await state.activateAudio();
      const project = get().project;

      // An armed instrument track records MIDI rather than audio: no microphone
      // is opened, and nothing is captured but the notes played.
      const armedInstrument = armedInstrumentTrack(project);
      if (armedInstrument) {
        heldNotes.clear();
        capturedNotes = [];
        capturedParams.clear();
        recordStartTicks = project.transport.playheadTicks;
        // The engine reports `playing` the moment it starts, and the store
        // takes its word for it — so recording is declared after, not before,
        // or the take would be marked playing and never capture a note.
        services.engine.play();
        services.playhead.setRunning(true);
        set({ recordingTrackId: armedInstrument, transportState: 'recording' });
        if (get().midiStatus !== 'open' && MidiInput.availability() === 'available') void get().enableMidi();
        return;
      }

      const armed = project.trackOrder.find((id) => project.tracks[id]?.armed && project.tracks[id]?.kind === 'audio');
      if (!armed) {
        state.notify({
          kind: 'warning',
          title: 'No track is armed',
          detail: 'Arm an audio track to record sound, or an instrument track to record MIDI.',
          sticky: false,
        });
        return;
      }

      const recorder = services.recorder();
      if (!recorder) {
        state.notify({ kind: 'error', title: 'Recording is unavailable', sticky: true });
        return;
      }

      try {
        const track = project.tracks[armed];
        await recorder.open(track.inputDeviceId);
        if (track.inputMonitor) recorder.setMonitor(services.engine.trackInput(armed));
        void get().refreshInputDevices();
      } catch (error) {
        state.notify({
          kind: 'error',
          title: 'Could not open the audio input',
          detail: error instanceof RecordingError ? error.message : describeError(error),
          sticky: true,
        });
        return;
      }

      set({ recordingTrackId: armed });
      recorder.start();
      services.engine.play();
      services.playhead.setRunning(true);
      set({ transportState: 'recording' });
      recordStartTicks = get().project.transport.playheadTicks;
    },

    // -- MIDI input --------------------------------------------------------

    async enableMidi() {
      await get().activateAudio();
      try {
        const devices = await services.midi.open();
        set({ midiStatus: 'open', midiDevices: devices });
        services.midi.onDevicesChanged((next) => set({ midiDevices: next }));
        get().notify({
          kind: 'success',
          title: devices.length > 0 ? `MIDI ready — ${devices.length} device(s)` : 'MIDI ready',
          detail:
            devices.length > 0
              ? undefined
              : 'No controller found. The computer keyboard plays the armed instrument track.',
          sticky: false,
        });
      } catch (error) {
        set({ midiStatus: MidiInput.availability() });
        get().notify({ kind: 'warning', title: 'MIDI is unavailable', detail: describeError(error), sticky: true });
      }
    },

    selectMidiDevice(deviceId) {
      services.midi.selectDevice(deviceId);
      set({ midiDeviceId: deviceId });
    },

    setKeyboardOctave(octave) {
      set({ keyboardOctave: Math.min(8, Math.max(-1, Math.round(octave))) });
    },

    /**
     * Plays a note straight through to the armed instrument, and captures it if
     * the transport is recording. Live notes go to the audio thread at
     * `currentTime` — this is the one place where "now" is the right answer,
     * because the performer's finger has already moved.
     */
    playLiveNote(pitch, velocity) {
      const trackId = armedInstrumentTrack(get().project);
      if (!trackId) return;
      const engine = services.engine;
      const ctx = engine.context;
      const instrument = engine.instrumentFor(trackId);
      if (!ctx || !instrument) return;

      // A restrike of a pitch the pedal is holding releases it first, so the
      // instrument never has two notes at one pitch and a recording never has
      // two overlapping ones.
      if (sustainPedal.noteOn(pitch)) releaseLiveNote(get, services, pitch);
      instrument.noteOn(pitch, velocity, ctx.currentTime);
      if (get().transportState === 'recording') {
        heldNotes.set(pitch, { startTicks: Math.max(0, Math.round(engine.currentTicks())), velocity });
      }
    },

    stopLiveNote(pitch) {
      // With the pedal down the release is kept, not passed on: the note keeps
      // sounding, and a recording keeps it held, until the pedal lifts.
      if (!sustainPedal.noteOff(pitch)) return;
      releaseLiveNote(get, services, pitch);
    },

    setSustainPedal(down) {
      for (const pitch of sustainPedal.set(down)) releaseLiveNote(get, services, pitch);
    },

    modWheelLive(amount) {
      // Like the bend: a gesture to the instrument, not an edit to the project,
      // and kept with the notes while a take is recorded.
      playLiveParam('modWheel', Math.max(0, Math.min(1, amount)));
    },

    bendLive(amount) {
      // A performance gesture, not an edit: it reaches the instrument the way
      // a note does, and never the project — except while recording, when it
      // is kept with the notes and written into the bend lane when the take
      // is. A wheel is sprung, so letting go sends it back to rest on its own.
      playLiveParam('bend', Math.max(-1, Math.min(1, amount)) * DEFAULT_BEND_RANGE_SEMITONES);
    },

    // -- projects ----------------------------------------------------------

    fadeOverlaps() {
      const found = bareOverlaps(get().project);
      if (found.length === 0) {
        get().notify({ kind: 'info', title: 'No overlaps to fade', sticky: false });
        return;
      }
      get().dispatch(command(CrossfadeCommands.fadeBareOverlaps, { trackIds: null, curve: null }));
      get().notify({
        kind: 'success',
        title: found.length === 1 ? 'Crossfaded one overlap' : `Crossfaded ${found.length} overlaps`,
        detail: 'Each fade covers the whole overlap, so nothing plays twice.',
        sticky: false,
      });
    },

    async newProject() {
      if (!(await confirmDiscard(get))) return;
      services.commands.clear();
      const project = createEmptyProject();
      await services.autosave.markSaved();
      set({ savedProjectId: null, selection: EMPTY_SELECTION });
      commit(project, { dirty: false, autosave: false });
      services.engine.seek(0);
    },

    async saveProject() {
      await get().captureWamStates();
      const project = get().project;
      try {
        await services.storage.saveProject(project);
        await services.storage.setSetting('lastProjectId', project.meta.id);
        await services.autosave.markSaved();
        set({ dirty: false, savedProjectId: project.meta.id });
        void get().refreshProjectList();
        get().notify({ kind: 'success', title: `Saved "${project.meta.name}"`, sticky: false });
      } catch (error) {
        get().notify({
          kind: 'error',
          title: 'The project could not be saved',
          detail: describeError(error),
          sticky: true,
        });
      }
    },

    async openProject(projectId) {
      if (!(await confirmDiscard(get))) return;
      try {
        const project = await services.storage.loadProject(projectId);
        if (!project) {
          get().notify({ kind: 'error', title: 'That project could not be found', sticky: false });
          return;
        }
        services.commands.clear();
        services.assets()?.clear();
        await services.storage.setSetting('lastProjectId', projectId);
        set({ savedProjectId: projectId, selection: EMPTY_SELECTION, modal: 'none' });
        commit(project, { dirty: false, autosave: false });
        services.engine.seek(project.transport.playheadTicks);
      } catch (error) {
        get().notify({
          kind: 'error',
          title: 'The project could not be opened',
          detail: describeError(error),
          sticky: true,
        });
      }
    },

    async deleteProject(projectId) {
      await services.storage.deleteProject(projectId);
      const collected = await services.storage
        .collectGarbage()
        .catch(() => ({ removedAssets: 0, removedRenders: 0, freedBytes: 0 }));
      await get().refreshProjectList();
      get().notify({
        kind: 'info',
        title: 'Project deleted',
        detail:
          collected.removedAssets > 0 || collected.removedRenders > 0
            ? `${collected.removedAssets} unused audio file(s) and ${collected.removedRenders} cached render(s) ` +
              `freed ${formatBytes(collected.freedBytes)}.`
            : undefined,
        sticky: false,
      });
    },

    async duplicateProject(projectId) {
      const source = await services.storage.loadProject(projectId);
      if (!source) return;
      const copy = cloneProject(source);
      copy.meta = {
        ...copy.meta,
        id: createEmptyProject().meta.id,
        name: `${source.meta.name} copy`,
        createdAt: Date.now(),
        modifiedAt: Date.now(),
      };
      await services.storage.saveProject(copy);
      await get().refreshProjectList();
    },

    async refreshProjectList() {
      try {
        set({ projectList: await services.storage.listProjects() });
      } catch {
        set({ projectList: [] });
      }
    },

    async acceptRecovery() {
      const offer = get().recoveryOffer;
      if (!offer) return;
      services.commands.clear();
      set({ recoveryOffer: null, savedProjectId: offer.snapshot.projectId, dirty: true });
      commit(offer.snapshot.project, { dirty: true, autosave: false });
      get().notify({
        kind: 'success',
        title: 'Recovered your unsaved work',
        detail: 'Your last saved version was left untouched. Save now to keep these changes.',
        sticky: true,
      });
    },

    async discardRecovery() {
      set({ recoveryOffer: null });
      await services.storage.clearRecovery().catch(() => undefined);
    },

    // -- feedback ----------------------------------------------------------

    notify(toast) {
      const id = toastId++;
      set({ toasts: [...get().toasts, { ...toast, id }] });
      if (!toast.sticky) setTimeout(() => get().dismissToast(id), 4000);
    },

    dismissToast(id) {
      set({ toasts: get().toasts.filter((t) => t.id !== id) });
    },
  };
});

let recordStartTicks = 0;
/** Notes currently held down while recording, keyed by pitch. */
const heldNotes = new Map<number, { startTicks: number; velocity: number }>();
const sustainPedal = new SustainPedal();

/** A note-off reaching the instrument, and the recording if one is running. */
function releaseLiveNote(get: () => DawState, services: ReturnType<typeof getServices>, pitch: number): void {
  const trackId = armedInstrumentTrack(get().project);
  if (!trackId) return;
  const engine = services.engine;
  const ctx = engine.context;
  engine.instrumentFor(trackId)?.noteOff(pitch, ctx?.currentTime ?? 0);

  const held = heldNotes.get(pitch);
  if (held) {
    heldNotes.delete(pitch);
    const endTicks = Math.max(held.startTicks + 1, Math.round(engine.currentTicks()));
    capturedNotes.push({ pitch, velocity: held.velocity, startTicks: held.startTicks, endTicks });
  }
}
let capturedNotes: { pitch: number; velocity: number; startTicks: number; endTicks: number }[] = [];
/** The wheels as they were moved during a MIDI recording, normalised for their lanes, by parameter. */
const capturedParams = new Map<string, { tick: number; value: number }[]>();

/** The armed instrument track, if any. */
function armedInstrumentTrack(project: Project): string | null {
  return (
    project.trackOrder.find(
      (id) => project.tracks[id]?.armed && project.tracks[id]?.kind === 'instrument' && project.tracks[id]?.instrument,
    ) ?? null
  );
}

/**
 * Ends a MIDI take: everything captured becomes one clip, added as a single
 * undo checkpoint so one press of Undo removes the take rather than a note.
 */
function finishMidiRecording(
  set: (patch: Partial<DawState>) => void,
  get: () => DawState,
  services: ReturnType<typeof getServices>,
): void {
  const trackId = get().recordingTrackId;
  const engine = services.engine;
  const endTicks = Math.round(engine.currentTicks());
  engine.pause();
  set({ transportState: 'stopped', recordingTrackId: null });

  // Notes still held when recording stopped end where the transport did.
  for (const [pitch, held] of heldNotes) {
    capturedNotes.push({ pitch, velocity: held.velocity, startTicks: held.startTicks, endTicks: Math.max(held.startTicks + 1, endTicks) });
  }
  heldNotes.clear();

  const notes = capturedNotes;
  const wheels = [...capturedParams].filter(([, points]) => points.length > 0);
  capturedNotes = [];
  capturedParams.clear();
  if (!trackId || (notes.length === 0 && wheels.length === 0)) return;

  const before = get().project;
  let project = before;
  let clipId: string | null = null;
  if (notes.length > 0) {
    const start = Math.min(recordStartTicks, ...notes.map((n) => n.startTicks));
    const finish = Math.max(...notes.map((n) => n.endTicks));
    clipId = createId('clp');

    project = runCommand(before, {
      type: 'clip.addMidi',
      params: {
        clipId,
        trackId,
        name: `${before.tracks[trackId]?.name ?? 'MIDI'} take`,
        startTicks: start,
        durationTicks: Math.max(1, finish - start),
      },
    }).project;

    for (const note of notes) {
      project = runCommand(project, {
        type: 'midi.addNote',
        params: {
          clipId,
          noteId: createId('not'),
          pitch: note.pitch,
          velocity: note.velocity,
          startTicks: note.startTicks - start,
          durationTicks: Math.max(1, note.endTicks - note.startTicks),
        },
      }).project;
    }
    project = archiveAsTake(project, trackId, clipId);
  }

  // Each wheel goes into its parameter's lane — the bend into the one a MIDI
  // file's bend arrives in — over the span of the recording: at rest from the
  // start until it first moved, then as it was played. One undo step with
  // the notes.
  for (const [paramId, points] of wheels) {
    project = writeParamLane(project, trackId, paramId, recordStartTicks, Math.max(endTicks, points[points.length - 1].tick), points);
  }

  services.commands.pushCheckpoint(before, 'Record MIDI take');
  set({
    project,
    dirty: true,
    selection: clipId ? { ...get().selection, clipIds: [clipId], trackId, noteIds: [] } : get().selection,
  });
  services.engine.applyProject(project);
  services.autosave.notifyChanged(project);
  get().notify({
    kind: 'success',
    title: 'MIDI take recorded',
    detail:
      `${notes.length} note(s) captured on this computer` +
      (wheels.length === 0 ? '.' : wheels.length === 1 ? ', and the wheel as it was played.' : ', and both wheels as they were played.'),
    sticky: false,
  });
}

/**
 * Writes a recorded wheel into the lane of the instrument parameter it drives,
 * adding the lane if the track has none. The wheel was at rest — the
 * parameter's default — from the start of the take until it first moved.
 */
function writeParamLane(
  project: Project,
  trackId: string,
  paramId: string,
  startTicks: number,
  endTicks: number,
  moves: { tick: number; value: number }[],
): Project {
  const track = project.tracks[trackId];
  const type = track?.instrument?.type;
  const descriptor = type ? instrumentParamDescriptor(type, paramId) : undefined;
  if (!track || !descriptor) return project;

  let laneId = track.automationLaneIds.find((id) => {
    const lane = project.automation[id];
    return lane && lane.target.kind === 'instrument' && lane.target.paramId === paramId;
  });
  let next = project;
  if (!laneId) {
    laneId = createId('lan');
    next = runCommand(next, {
      type: 'automation.addLane',
      params: { laneId, trackId, kind: 'instrument', effectId: null, paramId },
    }).project;
    if (!next.automation[laneId]) return project;
    next = runCommand(next, { type: 'automation.setVisible', params: { trackId, show: true } }).project;
  }
  const rest = normaliseParam(descriptor, descriptor.default);
  const points = moves[0].tick > startTicks ? [{ tick: startTicks, value: rest }, ...moves] : moves;
  return runCommand(next, {
    type: 'automation.writeRegion',
    params: { laneId, startTicks, endTicks, points },
  }).project;
}

async function finishRecording(
  set: (patch: Partial<DawState>) => void,
  get: () => DawState,
  services: ReturnType<typeof getServices>,
): Promise<void> {
  const recorder = services.recorder();
  const trackId = get().recordingTrackId;
  services.engine.pause();
  set({ transportState: 'stopped', recordingTrackId: null });

  const take = recorder?.stop() ?? null;
  await recorder?.close();
  if (!take || !trackId) return;

  const assets = services.assets();
  if (!assets) return;

  try {
    const before = get().project;
    const name = `${before.tracks[trackId]?.name ?? 'Take'} take`;
    const result = await assets.registerRecording(take.buffer, take.wav, name);

    const clipId = createId('clp');
    let project = runCommand(before, command(TransportCommands.registerAsset, { asset: result.asset })).project;
    project = runCommand(project, {
      type: ClipCommands.addAudioClip.type,
      params: {
        clipId,
        trackId,
        assetId: result.asset.id,
        name,
        startTicks: recordStartTicks,
        offsetSec: 0,
        durationSec: take.durationSec,
      },
    }).project;
    project = archiveAsTake(project, trackId, clipId);

    services.commands.pushCheckpoint(before, 'Record take');
    set({ project, dirty: true });
    services.engine.applyProject(project);
    services.autosave.notifyChanged(project);
    get().notify({
      kind: 'success',
      title: 'Take recorded',
      detail: `${take.durationSec.toFixed(1)}s captured and stored on this computer.`,
      sticky: false,
    });
  } catch (error) {
    get().notify({
      kind: 'error',
      title: 'The take could not be saved',
      detail: describeError(error),
      sticky: true,
    });
  }
}

async function confirmDiscard(get: () => DawState): Promise<boolean> {
  if (!get().dirty) return true;
  return window.confirm('This project has unsaved changes. Continue and lose them?');
}

/** `Song.mid` -> `Song`, for naming tracks after the file they came from. */
function stripExtension(name: string): string {
  const index = name.lastIndexOf('.');
  return index > 0 ? name.slice(0, index) : name;
}

function cloneClip(clip: Clip): Clip {
  return JSON.parse(JSON.stringify(clip)) as Clip;
}

export function describeError(error: unknown): string {
  if (error instanceof StorageError || error instanceof AudioImportError || error instanceof RecordingError) {
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return String(error);
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

/** Convenience selectors used across the UI. */
export const selectTracks = (state: DawState): Track[] =>
  state.project.trackOrder.map((id) => state.project.tracks[id]).filter(Boolean);

export const selectProjectEndTicks = (state: DawState): number => projectEndTicks(state.project);
